import React, { PropsWithChildren, Suspense, useEffect } from 'react';

import { Header } from 'modules/navigation/components';
import { Footer, ConnectionLost } from 'components';
import { AudioPlayer } from 'modules/audio-player';
import { useLocation } from 'react-router';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AudioAtoms } from 'modules/audios/recoil';
import { VideoAtoms } from 'modules/videos/recoil';
import { useRecoilValue } from 'recoil';
import { EventAtoms } from 'modules/events/recoil';
import { lazyNamed } from 'utils';

/** The layout is mounted for every member on every route, so importing these
 * directly would put the rich text editor, the file dropzone and the timezone
 * picker into everyone's bundle for the sake of screens only administrators
 * open. They are already rendered conditionally, so loading them on demand
 * costs nothing. */
const AudioCategoryForm = lazyNamed(
  () => import('modules/audios/components/AudioCategoryForm'),
  'AudioCategoryForm',
);
const AudioForm = lazyNamed(
  () => import('modules/audios/components/AudioForm'),
  'AudioForm',
);
const VideoCategoryForm = lazyNamed(
  () => import('modules/videos/components/VideoCategoryForm'),
  'VideoCategoryForm',
);
const VideoForm = lazyNamed(
  () => import('modules/videos/components/VideoForm'),
  'VideoForm',
);
const EventForm = lazyNamed(
  () => import('modules/events/components/EventForm'),
  'EventForm',
);

export const Layout: ReactFC<PropsWithChildren> = ({ children }) => {
  const location = useLocation();
  const audioCategoryFormOpen = useRecoilValue(AudioAtoms.categoryFormOpen);
  const audioFormOpen = useRecoilValue(AudioAtoms.audioFormOpen);
  const videoCategoryFormOpen = useRecoilValue(VideoAtoms.categoryFormOpen);
  const videoFormOpen = useRecoilValue(VideoAtoms.videoFormOpen);
  const eventFormOpen = useRecoilValue(EventAtoms.eventFormOpen);

  useEffect(() => {
    window.scrollTo({ behavior: 'smooth', top: 0, left: 0 });
  }, [location.pathname]);

  return (
    <React.Fragment>
      <div className="container">
        <Header />
        <main className="container__children">{children}</main>

        <AudioPlayer />

        {/** No fallback: these render through createPortal as modals, so an
          * inline loader would appear in the layout flow rather than over the
          * page. The chunk resolves faster than a spinner would be useful. */}
        <Suspense fallback={null}>
          {audioCategoryFormOpen && <AudioCategoryForm />}
          {audioFormOpen && <AudioForm />}
          {videoCategoryFormOpen && <VideoCategoryForm />}
          {videoFormOpen && <VideoForm />}
          {eventFormOpen && <EventForm />}
        </Suspense>

        <ConnectionLost />
        <Footer />
      </div>
      <ToastContainer />
    </React.Fragment>
  );
};
